/* eslint-disable max-statements */

import { Button, Group, LoadingOverlay, Stack } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useOverrideHooks } from '@override';
import moment from 'moment';
import { useState } from 'react';

import { BatchTypeEnum, EORIGIN_DATE_FORMAT_WITH_TIME, IBatchesCriteria, ReportStatus, SortDirection, TemplateType } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { SearchBox, Table, TableActions } from '@components';
import { BatchView } from '@features/batches';
import { useBatchesCustomsReport } from '@hooks/batch';
import { removeUndefined } from '@utils';
import { NewReportRequest } from './NewReportRequest';

export const CustomsReports = () => {
  const OverrideHooks = useOverrideHooks();
  const downloadBatchReportMutation = OverrideHooks.useDownloadBatchReport();

  const criteria = {
    filters: { templateType: TemplateType.HIGH_VALUE_H1, customsReport: true },
    pagination: { page: 1, size: 10, direction: 1 },
    sorting: { field: 'reports.customsReport.requestDate', direction: SortDirection.DESC },
  };

  const [filters, setFilters] = useState<IBatchesCriteria['filters']>(criteria.filters);
  const [sorting, setSorting] = useState<IBatchesCriteria['sorting']>(criteria.sorting);
  const [pagination, setPagination] = useState<IBatchesCriteria['pagination']>(criteria.pagination);
  const { data: { list: customsReportsList, total: totalCustomsReportsCount } = { list: [], total: 0 }, isLoading } = useBatchesCustomsReport({
    filters,
    sorting,
    pagination,
  });

  const [batchView, setBatchView] = useState(BatchTypeEnum.HIGH_VALUES);

  const setBatchesFilters = (updatedFilters: Partial<IBatchesCriteria['filters']>) => {
    setFilters((prevFilters) => removeUndefined({ ...prevFilters, ...updatedFilters }));
  };

  const searchBatches = (searchQuery: string) => {
    setBatchesFilters({ searchQuery });
    setPagination({ page: 1, size: pagination.size });
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
  };

  const extractStatus = (row) => {
    const userFriendlyStatus = (status: string) =>
      status
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase());

    const addCount = (status) => `${status} (${row.reports?.customsReport?.lrns?.length || 0}/${row.reports?.customsReport?.lrnsTotal || 'N/A'})`;

    return addCount(userFriendlyStatus(row.reports?.customsReport.status));
  };

  const columns = [
    {
      name: 'Batch name',
      selector: 'name',
      cell: (row) => row.name,
      sortable: true,
    },
    {
      name: 'Request date',
      selector: 'reports.customsReport.requestDate',
      cell: (row) => moment(row.reports?.customsReport?.requestDate).format(EORIGIN_DATE_FORMAT_WITH_TIME),
      sortable: true,
    },
    {
      name: 'Status',
      cell: (row) => extractStatus(row),
    },
    {
      name: 'Actions',
      sticky: 'right' as any,
      cell: (row) => (
        <TableActions
          actions={
            row.reports?.customsReport?.status === ReportStatus.DONE
              ? [
                  {
                    icon: IconFactory.download,
                    text: 'Download',
                    onClick: () => downloadBatchReportMutation.mutate({ batchId: row._id }),
                  },
                ]
              : []
          }
          disableAfterClick={false}
        />
      ),
    },
  ];

  const openNewReportRequest = () => {
    modals.open({
      title: 'New customs report request for released batches',
      children: <NewReportRequest onClose={() => modals.closeAll()} />,
      size: 'lg',
      centered: true,
    });
  };

  if (isLoading) {
    return <LoadingOverlay visible overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <Stack>
      <Group justify="flex-end" mb="md">
        <BatchView
          value={batchView}
          onChange={(item) => {
            setBatchView(item);
            setBatchesFilters({
              templateType: item === BatchTypeEnum.LOW_VALUES ? TemplateType.LOW_VALUE_H7 : TemplateType.HIGH_VALUE_H1,
              isExport: item === BatchTypeEnum.HIGH_VALUES_EXPORT ? true : null,
            });
          }}
        />
        <Button onClick={openNewReportRequest} variant="outline" size="md" h={55}>
          New customs reports request
        </Button>
        <SearchBox handleSearch={searchBatches} value={filters?.searchQuery} placeholder="Search by batch name" />
      </Group>

      <Table
        columns={columns}
        dataSource={customsReportsList ?? []}
        total={totalCustomsReportsCount}
        onPaginationChange={handlePagination}
        onSortingChange={handleSorting}
        isLoading={isLoading}
        defaultPage={pagination.page}
        defaultPerPage={pagination.size}
        defaultSorting={sorting.field}
        defaultSortingDirection={sorting.direction}
      />
    </Stack>
  );
};
